import React from 'react';
import { motion } from 'framer-motion';
import { Check, BookOpen, Clock, Activity, ShieldCheck, FileText } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
export function ProductReveal() {
  return <section className="w-full bg-cream py-20 px-6" data-id="element-112">
      <div className="max-w-reading mx-auto space-y-24" data-id="element-113">
        {/* Intro */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="text-center space-y-8" data-id="element-114">
          <span className="text-amber font-bold tracking-widest uppercase text-sm" data-id="element-115">
            The Solution
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-charcoal" data-id="element-116">
            Introducing: The 7-Day Minimum Viable Calm Reset
          </h2>

          <div className="bg-white p-8 md:p-12 rounded-2xl border-2 border-amber/30 shadow-lg relative my-12" data-id="element-117">
            <p className="text-lg text-charcoal-muted mb-6" data-id="element-118">
              Minimum Viable Calm (MVC) is the only standard you need right now:
            </p>
            <blockquote className="font-serif text-2xl md:text-3xl font-medium text-charcoal italic leading-relaxed" data-id="element-119">
              The smallest amount of regulation that creates a real shift.
            </blockquote>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-lg mx-auto" data-id="element-120">
            {['Not perfect calm.', 'Not “healed forever.”', 'Not a new personality.'].map((text, i) => <div key={i} className="flex items-center gap-3 text-charcoal-muted" data-id="element-121">
                <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-122" />
                <span data-id="element-123">{text}</span>
              </div>)}
          </div>

          <div className="text-left bg-teal/5 p-6 rounded-xl border border-teal/10 mt-8" data-id="element-124">
            <p className="font-bold text-teal-dark mb-4" data-id="element-125">
              Just enough calm to:
            </p>
            <ul className="space-y-3" data-id="element-126">
              {['make a clear decision', 'speak without biting', 'stop spiraling at night', 'recover faster when life hits'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg text-charcoal" data-id="element-127">
                  <Check className="w-5 h-5 text-teal" data-id="element-128" />
                  <span data-id="element-129">{item}</span>
                </li>)}
            </ul>
          </div>
        </motion.div>

        {/* The Tools */}
        <div className="space-y-12" data-id="element-130">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center" data-id="element-131">
            <h3 className="font-serif text-3xl font-bold mb-4" data-id="element-132">
              What you’ll use (and why it works)
            </h3>
            <p className="text-xl text-charcoal-muted" data-id="element-133">
              This isn’t theory. It’s a protocol.
            </p>
          </motion.div>

          <div className="grid gap-8" data-id="element-134">
            {[{
            icon: ShieldCheck,
            title: '1) The 90-Second Rescue Kit',
            subtitle: '(for spikes)',
            desc: 'For the moment you’re already anxious—when you can’t “talk yourself down.” It interrupts escalation using simple, mechanical steps.'
          }, {
            icon: Clock,
            title: '2) The 7-Day 10-Minute Reset',
            subtitle: '(for baseline)',
            desc: 'One short daily reset that lowers your baseline over a week—without requiring a lifestyle overhaul.'
          }, {
            icon: Activity,
            title: '3) The Anxiety Pattern Map',
            subtitle: '(so you stop guessing)',
            desc: 'Because anxiety isn’t random. It’s patterned. You’ll identify your trigger loop and stop treating every day like a new battle.'
          }].map((tool, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-teal flex flex-col md:flex-row gap-6" data-id="element-135">
                <div className="bg-cream p-4 rounded-full w-16 h-16 flex items-center justify-center flex-shrink-0" data-id="element-136">
                  <tool.icon className="w-8 h-8 text-teal-dark" data-id="element-137" />
                </div>
                <div data-id="element-138">
                  <h4 className="font-serif text-2xl font-bold text-charcoal mb-1" data-id="element-139">
                    {tool.title}{' '}
                    <span className="text-charcoal-muted font-normal text-lg italic block md:inline" data-id="element-140">
                      {tool.subtitle}
                    </span>
                  </h4>
                  <p className="text-lg text-charcoal leading-relaxed mt-3" data-id="element-141">
                    {tool.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* What to Expect */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="bg-cream-dark p-8 md:p-12 rounded-2xl space-y-8" data-id="element-142">
          <h3 className="font-serif text-3xl font-bold text-charcoal" data-id="element-143">
            What to expect in 7 days{' '}
            <span className="text-charcoal-muted font-normal italic text-xl" data-id="element-144">
              (realistic, not magical)
            </span>
          </h3>

          <p className="text-lg" data-id="element-145">
            This isn’t a promise that life becomes easy. It’s a promise that
            your internal world becomes less expensive.
          </p>

          <p className="font-medium text-teal-dark" data-id="element-146">
            Most women notice things like:
          </p>

          <ul className="grid gap-4" data-id="element-147">
            {['spikes end sooner', 'less jaw/shoulder tension', 'less nighttime spiraling', 'fewer “hair-trigger” moments', 'more ability to choose a response instead of being hijacked'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-148">
                <div className="mt-1 w-2 h-2 rounded-full bg-amber flex-shrink-0" data-id="element-149" />
                <span className="text-lg text-charcoal" data-id="element-150">{item}</span>
              </li>)}
          </ul>

          <div className="pt-6 border-t border-gray-200" data-id="element-151">
            <p className="text-xl font-serif italic text-charcoal text-center" data-id="element-152">
              It’s not about never feeling anxiety again. <br data-id="element-153" />
              <span className="font-bold not-italic text-teal-dark" data-id="element-154">
                It’s about getting your steering wheel back.
              </span>
            </p>
          </div>
        </motion.div>

        {/* What's Inside */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="space-y-8" data-id="element-155">
          <h3 className="font-serif text-3xl font-bold text-center" data-id="element-156">
            What’s inside (exactly)
          </h3>
          <p className="text-center text-charcoal-muted" data-id="element-157">
            When you purchase, you get the full PDF ebook including:
          </p>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-158">
            {['A clear explanation of “alarm mode” (without fluff)', 'MVC: how to create a measurable downshift', 'The Anxiety Pattern Map (identify your loop)', 'The 90-second Rescue Kit script (copy/paste)', 'The 7-day plan (10 minutes/day)', 'Simple trackers (printable or phone-friendly)', '“Day 8” plan (so you don’t slide back into the old loop)'].map((item, i) => <div key={i} className="flex items-center gap-4 p-5 border-b border-gray-100 last:border-0 hover:bg-cream transition-colors" data-id="element-159">
                <FileText className="w-5 h-5 text-teal flex-shrink-0" data-id="element-160" />
                <span className="text-lg text-charcoal" data-id="element-161">{item}</span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}